'use strict'

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { fetchIrrigationCrews } from '../../PropertyDetail/irrigationCrews'
import { useLocation } from 'react-router-dom'

const TerritoryContext = createContext(null)

/**
 * Provides shared territory workbench state to all tabs and sub-components.
 *
 * Manages: scenarios, crews/routes, scenario metrics, property counts.
 * Does NOT manage: modal state, tab-specific fetches, UI state.
 */
export function TerritoryProvider ({ url, children }) {
  const location = useLocation()

  // ── Scenarios ───────────────────────────────────────────────
  const [scenarios, setScenarios] = useState([])
  const [selectedScenarioId, setSelectedScenarioId] = useState('')
  const [loadingScenarios, setLoadingScenarios] = useState(true)
  const [error, setError] = useState(null)

  const requestedScenarioId = useMemo(
    () => (new URLSearchParams(location.search).get('scenarioId') || '').trim(),
    [location.search]
  )

  const selectedScenario = useMemo(
    () => scenarios.find(item => item._id === selectedScenarioId) || null,
    [scenarios, selectedScenarioId]
  )

  const isScenarioEditable = selectedScenario?.status === 'draft'

  // ── Crews & Routes ──────────────────────────────────────────
  const [crews, setCrews] = useState([])
  const [routes, setRoutes] = useState([])
  // Irrigation-division crews for the "Assigned Irrigation Tech" picker only —
  // kept separate from the Maintenance `crews` above (route/day assignment).
  const [irrigationCrews, setIrrigationCrews] = useState([])

  const crewIds = useMemo(() => {
    return [...crews]
      .sort((a, b) => String(a.name || a.foremanName || a._id).localeCompare(String(b.name || b.foremanName || b._id)))
      .map(c => String(c._id))
  }, [crews])

  const routeIds = useMemo(() => {
    return [...routes]
      .sort((a, b) => String(a.name || a._id).localeCompare(String(b.name || b._id)))
      .map(r => String(r._id))
  }, [routes])

  // ── Scenario Metrics ────────────────────────────────────────
  const [scenarioMetrics, setScenarioMetrics] = useState(null)
  const [loadingMetrics, setLoadingMetrics] = useState(false)

  // ── Property Counts ─────────────────────────────────────────
  const [propertyCounts, setPropertyCounts] = useState({ total: 0, vip: 0, locked: 0, unassigned: 0, unlinked: 0, anchors: 0 })

  // ── Snackbar ────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const closeSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }))
  }, [])

  // ── Fetch Functions ─────────────────────────────────────────

  const fetchScenarios = useCallback(async (preferredScenarioId = null) => {
    setLoadingScenarios(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios`)
      const nextScenarios = response.data?.scenarios || []
      setScenarios(nextScenarios)

      if (preferredScenarioId && nextScenarios.some(item => item._id === preferredScenarioId)) {
        setSelectedScenarioId(preferredScenarioId)
      } else {
        setSelectedScenarioId(prev => {
          if (prev && nextScenarios.some(item => item._id === prev)) return prev
          const draft = nextScenarios.find(item => item.status === 'draft')
          return draft?._id || nextScenarios[0]?._id || ''
        })
      }
    } catch (err) {
      setError(err.message || 'Failed to load territory scenarios')
    } finally {
      setLoadingScenarios(false)
    }
  }, [url])

  const fetchCrews = useCallback(async (scenarioId) => {
    try {
      const response = await axios.get(`${url}/api/hmi/territory/properties`, {
        params: scenarioId ? { scenarioId } : undefined
      })
      const fetchedCrews = response.data?.crews || []
      const fetchedRoutes = response.data?.routes || []
      setCrews(fetchedCrews)
      setRoutes(fetchedRoutes)
    } catch (_err) {
      // silently ignored — matches original behavior
    }
  }, [url])

  // Irrigation-division crews are not scenario-scoped — fetched once.
  const fetchIrrigationCrewsList = useCallback(async () => {
    try {
      setIrrigationCrews(await fetchIrrigationCrews(url))
    } catch (_err) {
      setIrrigationCrews([])
    }
  }, [url])

  const fetchMetrics = useCallback(async (scenarioId) => {
    setLoadingMetrics(true)
    try {
      const metricsUrl = scenarioId
        ? `${url}/api/hmi/territory/scenarios/${scenarioId}/metrics`
        : `${url}/api/hmi/territory/metrics`
      const response = await axios.get(metricsUrl)
      setScenarioMetrics({
        crews: response.data?.crews || [],
        routes: response.data?.routes || [],
        fleet: response.data?.fleet || null
      })
    } catch (_err) {
      setScenarioMetrics(null)
    } finally {
      setLoadingMetrics(false)
    }
  }, [url])

  const refreshAll = useCallback(async () => {
    await fetchScenarios(selectedScenarioId || null)
  }, [fetchScenarios, selectedScenarioId])

  // ── Effects ─────────────────────────────────────────────────

  useEffect(() => {
    fetchScenarios(requestedScenarioId || null)
  }, [fetchScenarios, requestedScenarioId])

  useEffect(() => {
    fetchCrews(selectedScenarioId || null)
    fetchMetrics(selectedScenarioId || null)
  }, [selectedScenarioId, fetchCrews, fetchMetrics])

  useEffect(() => {
    fetchIrrigationCrewsList()
  }, [fetchIrrigationCrewsList])

  // ── Context Value ───────────────────────────────────────────

  const value = useMemo(() => ({
    // Scenario state
    url,
    scenarios,
    selectedScenarioId,
    setSelectedScenarioId,
    selectedScenario,
    isScenarioEditable,
    loadingScenarios,
    error,

    // Crews & routes
    crews,
    routes,
    irrigationCrews,
    crewIds,
    routeIds,

    // Metrics
    scenarioMetrics,
    loadingMetrics,

    // Property counts (set by PropertiesTab)
    propertyCounts,
    setPropertyCounts,

    // Snackbar
    snackbar,
    showSnackbar,
    closeSnackbar,

    // Fetch functions
    fetchScenarios,
    fetchCrews,
    fetchMetrics,
    refreshAll
  }), [
    url,
    scenarios, selectedScenarioId, selectedScenario, isScenarioEditable, loadingScenarios, error,
    crews, routes, irrigationCrews, crewIds, routeIds,
    scenarioMetrics, loadingMetrics,
    propertyCounts, snackbar,
    showSnackbar, closeSnackbar,
    fetchScenarios, fetchCrews, fetchMetrics, refreshAll
  ])

  return (
    <TerritoryContext.Provider value={value}>
      {children}
    </TerritoryContext.Provider>
  )
}

export function useTerritory () {
  const ctx = useContext(TerritoryContext)
  if (!ctx) {
    throw new Error('useTerritory must be used within a <TerritoryProvider>')
  }
  return ctx
}

export default TerritoryContext
